<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("home.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("home.error") }}</div>
    <template v-else>
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("home.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("home.title") }}</div>
        </div>
        <!-- G1: a period selector used to sit here bound to NOTHING — no
             v-model, no handler. Changing it appeared to re-scope the whole
             dashboard and changed not one number, which is its own way of
             making the data look wrong. GET /reports/dashboard takes no period
             parameter, so there is nothing to wire it to; the honest thing is
             not to offer the control. (`home.period.*` keys are left in place
             for whenever the endpoint does support a window.) -->
      </div>

      <!-- KPI cards -->
      <div class="kpis">
        <div v-for="(k, i) in kpis" :key="i" class="kpi">
          <div class="ic" :style="{ background: k.bg }">{{ k.icon }}</div>
          <div class="v mono">{{ fInt(k.value) }}</div>
          <div class="l">{{ k.label }}</div>
        </div>
      </div>

      <!-- charts -->
      <div class="grid2 mt">
        <div class="panel">
          <div class="panel__head">
            <h4>📈 {{ $t("home.charts.monthlyTitle") }}</h4>
            <span class="tag muted">2026</span>
          </div>
          <div class="panel__body">
            <div class="chart">
              <div v-for="(bar, i) in bars" :key="i" class="bar">
                <div class="fill" :style="{ height: bar.pct + '%' }" :title="fInt(bar.count)"></div>
                <span class="lb">{{ monthLabel(bar.month) }}</span>
              </div>
            </div>
            <div class="legend">
              <span><i style="background: var(--v-brand)"></i> {{ $t("home.charts.monthlyLegend") }}</span>
            </div>
          </div>
        </div>
        <div class="panel">
          <div class="panel__head"><h4>🍩 {{ $t("home.charts.donutTitle") }}</h4></div>
          <div class="panel__body">
            <div class="donut" :style="{ background: donutData.gradient }">
              <div class="hole">
                <b class="mono">{{ fInt(donutData.total) }}</b>
                <span>{{ $t("home.charts.employeeUnit") }}</span>
              </div>
            </div>
            <div class="legend" style="justify-content: center">
              <span v-for="(s, i) in donutData.legend" :key="i">
                <i :style="{ background: s.color }"></i>
                {{ statusLabel(s.status) }} {{ fInt(s.count) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- policy card + quick actions -->
      <div class="grid2 mt">
        <div class="panel">
          <div class="panel__head">
            <h4>📄 {{ $t("home.policy.title") }}</h4>
            <!-- G1: this tag was hardcoded "active" and claimed a suspended or
                 expired policy was live. The backend now sends policy.status. -->
            <span v-if="policy" class="tag" :class="policyTag" data-test="policy-status">
              <span class="dotc"></span> {{ policyStatusLabel }}
            </span>
          </div>
          <div class="panel__body">
            <div class="row between">
              <span class="muted sm">{{ $t("home.policy.number") }}</span>
              <b class="mono">{{ policy ? policy.number : "—" }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("home.policy.validity") }}</span>
              <b>{{ policy ? fDate(policy.validFrom) + " — " + fDate(policy.validTo) : "—" }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("home.policy.contract") }}</span>
              <span class="tag paid">📎 {{ contract ? contract.fileRef : "—" }}</span>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("home.policy.insurer") }}</span>
              <b>{{ report ? report.insurerName : "—" }}</b>
            </div>
          </div>
        </div>
        <div class="panel">
          <div class="panel__head"><h4>⚡ {{ $t("home.actions.title") }}</h4></div>
          <div class="panel__body">
            <div class="row" style="gap: 10px">
              <button class="btn primary" type="button" @click="go('employees-add')">➕ {{ $t("home.actions.newEmployee") }}</button>
              <button class="btn accent" type="button" @click="go('employees-import')">📥 {{ $t("home.actions.uploadExcel") }}</button>
            </div>
            <div class="divider"></div>
            <h5 class="sm" style="margin-bottom: 8px">{{ $t("home.actions.recentTitle") }}</h5>
            <div class="acts">
              <div v-for="a in activities" :key="a.id" class="act">
                <span class="b">💸</span>
                {{ a.employeeName }} — {{ a.serviceType }}
                <b class="mono">&nbsp;{{ fInt(a.amount) }}&nbsp;</b>
                <span class="tag" :class="claimTag(a.status)">{{ claimStatusLabel(a.status) }}</span>
              </div>
              <!-- Without this, no claims looked identical to a broken widget. -->
              <div v-if="activities.length === 0" class="muted sm" data-test="recent-empty">
                {{ $t("home.actions.recentEmpty") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet } from "../../api/vivo/http";
import { barData, donut } from "./home-vm";

const CLAIM_TAG = {
  registered: "new",
  at_insurer: "review",
  missing_doc: "muted",
  accepted: "ok",
  rejected: "danger",
};

export default {
  name: "VivoHome",
  data() {
    return { report: null, loading: true, error: false };
  },
  async mounted() {
    try {
      this.report = await apiGet("/reports/dashboard");
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    kpis() {
      const k = this.report && this.report.kpis;
      if (!k) return [];
      return [
        { icon: "🛡️", bg: "var(--v-brand-soft)", value: k.insured, label: this.$t("home.kpi.insured") },
        { icon: "✅", bg: "var(--v-ok-soft)", value: k.active, label: this.$t("home.kpi.active") },
        { icon: "⏸️", bg: "var(--v-warn-soft)", value: k.suspendedLeft, label: this.$t("home.kpi.suspendedLeft") },
        { icon: "📋", bg: "var(--v-accent-soft)", value: k.openClaims, label: this.$t("home.kpi.openClaims") },
      ];
    },
    bars() {
      return this.report ? barData(this.report.employeesByMonth) : [];
    },
    donutData() {
      return this.report ? donut(this.report.employeesByStatus) : { gradient: "", legend: [], total: 0 };
    },
    policy() {
      return this.report && this.report.policy;
    },
    contract() {
      return this.report && this.report.contract;
    },
    activities() {
      // G1: `recentActivities` is the HR action log (employeeStatusHistory) —
      // rows with no employeeName, serviceType, amount or status, so this list
      // rendered a column of blanks, "0" and an undefined status label.
      // `recentClaims` is the claim-shaped field; the fallback keeps an older
      // deployed backend rendering rather than showing nothing.
      const r = this.report || {};
      return r.recentClaims || r.recentActivities || [];
    },
    policyTag() {
      const st = this.policy && this.policy.status;
      return { active: "ok", suspended: "review", expired: "muted", cancelled: "danger" }[st] || "muted";
    },
    policyStatusLabel() {
      const st = (this.policy && this.policy.status) || "";
      const key = "home.policy.statuses." + st;
      // An unknown status shows its raw value rather than a missing-key string:
      // the status list is business config and can grow without a FE release.
      return this.$te(key) ? this.$t(key) : st || this.$t("home.policy.statusUnknown");
    },
  },
  methods: {
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
    monthLabel(month) {
      return new Intl.DateTimeFormat(this.$i18n.locale, { month: "short", numberingSystem: "latn" }).format(
        new Date(month + "-01T00:00:00Z"),
      );
    },
    statusLabel(status) {
      return this.$t("home.status." + status);
    },
    claimTag(status) {
      return CLAIM_TAG[status] || "muted";
    },
    claimStatusLabel(status) {
      return this.$t("home.claimStatus." + status);
    },
    go(name) {
      if (this.$route.name !== name) this.$router.push({ name });
    },
  },
};
</script>
